import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import { API_BASE_URL, STORAGE_KEYS } from './config'
import { redirectToLogin, refreshJWTToken, type ResultatJeton } from './auth'
import { useAuthStore } from '@/stores/useAuthStore'
import { useConnexionStore } from '@/stores/useConnexionStore'
import i18n from '@/i18n'
import { defaultLocale, locales } from '@/lib/locales'

const PUBLIC_PATHS = ['/api/token/', '/api/token/refresh/', '/api/auth/registration/', '/api/auth/login/', '/api/auth/request-reset-code/', '/api/auth/verify-reset-code/']

function isPublicPath(url: string | undefined): boolean {
  if (!url) return false
  return PUBLIC_PATHS.some((p) => url.includes(p))
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken
  if (token) config.headers.Authorization = `Bearer ${token}`
  // The server answers in the language of the screen, not the browser's (its messages follow Accept-Language)
  if (!config.headers['Accept-Language']) {
    config.headers['Accept-Language'] = (locales as readonly string[]).includes(i18n.language) ? i18n.language : defaultLocale
  }
  return config
})

let isRefreshing = false
let refreshPromise: Promise<ResultatJeton> | null = null
let isRedirecting = false

interface RetriableConfig extends AxiosRequestConfig {
  _retry?: boolean
}

// Panne = pas de réponse du tout, trop de requêtes, ou serveur en façade HS.
// Une 500 isolée est un bogue d'une fonction, pas une panne : pas de bandeau.
const STATUTS_PANNE = [429, 502, 503, 504]

// Un 502-504 écrit par Callizy lui-même (JSON) dit qu'un service relié
// (Clio, Stripe…) a refusé ou tardé : Callizy répond, ce n'est pas une panne.
// Le 4 nov. 2026, un refus de Clio en 502 allumait le bandeau ; son retour
// rechargeait tout l'écran, le 502 revenait, jusqu'au 429 pour tout le compte.
function estPanne(error: AxiosError): boolean {
  const reponse = error.response
  if (!reponse || reponse.status === 429) return true
  if (!STATUTS_PANNE.includes(reponse.status)) return false
  return !String(reponse.headers?.['content-type'] ?? '').includes('application/json')
}

api.interceptors.response.use(
  (response) => {
    useConnexionStore.getState().setInjoignable(false)
    return response
  },
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined
    const status = error.response?.status

    if (!axios.isCancel(error)) {
      if (estPanne(error)) {
        useConnexionStore.getState().setInjoignable(true)
        // Les écrans affichent error.message : jamais « Network Error » ni
        // « Request failed with status code 503 » sous les yeux d'un client.
        error.message = i18n.t('serveurInjoignable.title', 'Callizy ne répond pas pour le moment')
      } else {
        useConnexionStore.getState().setInjoignable(false)
        if (STATUTS_PANNE.includes(status!)) {
          error.message = i18n.t('serveurInjoignable.serviceRelie', "Un service relié à Callizy n'a pas répondu. Réessayez dans un instant.")
        }
      }
    }

    // L'onboarding d'une entreprise déjà configurée est refusé par le serveur
    // (409 onboarding_termine). Rien à corriger pour le client : on le ramène
    // à son tableau de bord au lieu de lui montrer une erreur technique.
    const refus = error.response?.data as { error?: string; message?: string } | undefined
    if (status === 409 && refus?.error === 'onboarding_termine') {
      if (refus.message) error.message = refus.message
      if (!isRedirecting) {
        isRedirecting = true
        const segment = window.location.pathname.split('/')[1]
        const locale = (locales as readonly string[]).includes(segment) ? segment : defaultLocale
        window.location.replace(`/${locale}/dashboard`)
      }
      return Promise.reject(error)
    }

    // Risky sign-in not confirmed: every route sends the person back to the
    // code screen (the server refuses everything else until then).
    if (status === 403 && refus?.error === 'verification_required') {
      const segment = window.location.pathname.split('/')[1]
      const locale = (locales as readonly string[]).includes(segment) ? segment : defaultLocale
      if (!isRedirecting && !window.location.pathname.endsWith('/verification')) {
        isRedirecting = true
        window.location.replace(`/${locale}/verification`)
      }
      return Promise.reject(error)
    }

    if (!original || status !== 401 || isPublicPath(original.url) || original._retry) {
      return Promise.reject(error)
    }

    const data = error.response?.data as { code?: string; detail?: string } | undefined

    if (data?.code === 'user_not_found' || data?.detail === 'User not found') {
      localStorage.setItem(STORAGE_KEYS.ACCOUNT_DELETED, 'true')
      if (!isRedirecting) {
        isRedirecting = true
        redirectToLogin('invalid')
      }
      return Promise.reject(error)
    }

    if (data?.code !== 'token_not_valid') {
      return Promise.reject(error)
    }

    if (!isRefreshing) {
      isRefreshing = true
      refreshPromise = refreshJWTToken().finally(() => {
        isRefreshing = false
      })
    }
    const resultat = await refreshPromise!
    refreshPromise = null

    // Serveur injoignable : on garde la session, l'erreur remonte à l'écran
    // (« Connexion difficile »), jamais une déconnexion à tort.
    if (resultat === 'indisponible') return Promise.reject(error)

    if (resultat === 'expire') {
      if (!isRedirecting) {
        isRedirecting = true
        redirectToLogin('expired')
      }
      return Promise.reject(error)
    }

    original._retry = true
    const newToken = useAuthStore.getState().accessToken
    if (newToken && original.headers) {
      ;(original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`
    }
    return api.request(original)
  },
)
